import React, { useEffect, useState } from "react";
import { McTable } from "@maersk-global/mds-react-wrapper/components-core";
import StatTile, { formatCount } from "../StatTile";
import WarningBanner from "../WarningBanner";
import FunnelChart from "../charts/FunnelChart";
import TrendLineChart from "../charts/TrendLineChart";
import { getE2ETimelinessPerformance } from "../../../services/metricsApi";
import "../Analytics.css";

const FUNNEL_TABLE_COLUMNS = [
  { id: "label", label: "Stage" },
  { id: "sla", label: "SLA" },
  { id: "reached_count", label: "Reached" },
  { id: "within_sla_count", label: "Within SLA" },
  { id: "within_sla_pct", label: "Within SLA %" },
  { id: "avg_days", label: "Avg Days" },
];

// §7 E2E Timeliness Performance — response shape is confirmed, so fields are
// read directly rather than hedged via pick() as in inferred dashboards.
const E2ETimelinessSection = ({ id, filters, refreshKey }) => {
  const [state, setState] = useState({ loading: true, data: null, error: null });

  useEffect(() => {
    let cancelled = false;
    setState((prev) => ({ ...prev, loading: true }));

    getE2ETimelinessPerformance(filters)
      .then((data) => {
        if (!cancelled) setState({ loading: false, data, error: null });
      })
      .catch((err) => {
        if (!cancelled) setState({ loading: false, data: null, error: err.message });
      });

    return () => {
      cancelled = true;
    };
  }, [filters, refreshKey]);

  const { loading, data, error } = state;
  const kpi = data?.kpi_cards || {};
  const funnel = data?.funnel || [];

  const funnelStages = funnel.map((stage) => ({
    key: stage.stage,
    label: stage.label,
    count: stage.reached_count || 0,
    sla: stage.sla_days != null && stage.sla_unit
      ? `SLA: ${stage.sla_days} ${stage.sla_unit}`
      : null,
  }));

  const stageTrendSeries = [
    {
      key: "avg_days",
      label: "Avg Stage Duration (days)",
      values: funnel.map((stage) => Number(stage.avg_days) || 0),
    },
  ];
  const stageTrendLabels = funnel.map((stage) => stage.label);

  const funnelTableData = funnel.map((stage) => ({
    ...stage,
    _id: stage.stage,
    sla: stage.sla_days != null && stage.sla_unit
      ? `${stage.sla_days} ${stage.sla_unit}`
      : "-",
  }));

  return (
    <section id={id} className="analytics-section">
      <div className="analytics-section-header">
        <h2 className="analytics-section-heading">E2E Timeliness Performance</h2>
        <span className="analytics-section-caption">Creation &rarr; Invoice Creation &middot; SLA varies by stage</span>
      </div>

      <WarningBanner warning={data?.warning} />
      {error && <div className="overview-card-error">Failed to load E2E Timeliness Performance: {error}</div>}
      {loading && !data && <div className="overview-card-loading">Loading…</div>}

      {data && (
        <>
          <div className="stat-tile-row">
            <StatTile
              label="Within SLA (End-to-End)"
              value={kpi.within_sla_pct !== undefined ? `${Number(kpi.within_sla_pct).toFixed(1)}%` : "–"}
              status={kpi.within_sla_pct >= 90 ? "good" : kpi.within_sla_pct >= 70 ? "warning" : "critical"}
            />
            <StatTile label="Within SLA Count" value={formatCount(kpi.within_sla_count)} />
            <StatTile label="Total Completed" value={formatCount(kpi.total_completed)} />
            <StatTile label="Avg Lifecycle (Days)" value={kpi.avg_lifecycle_days !== undefined ? Number(kpi.avg_lifecycle_days).toFixed(1) : "–"} />
          </div>

          <div className="analytics-charts-row">
            <div className="analytics-section-card">
              <h4 className="analytics-section-title">Lifecycle Funnel</h4>
              <FunnelChart stages={funnelStages} />
            </div>

            <div className="analytics-section-card">
              <h4 className="analytics-section-title">Avg Stage Duration Trend</h4>
              <TrendLineChart
                series={stageTrendSeries}
                xLabels={stageTrendLabels}
                valueFormatter={(v) => `${Number(v).toFixed(1)}d`}
              />
            </div>
          </div>

          <div className="analytics-section-card">
            <details className="analytics-collapsible" open>
              <summary>Funnel Detail — {funnelTableData.length} stages</summary>
              <McTable data={funnelTableData} datakey="_id" zebrastripes truncate fit="small" columns={FUNNEL_TABLE_COLUMNS} />
            </details>
          </div>
        </>
      )}
    </section>
  );
};

export default E2ETimelinessSection;
